import {
    ArcElement, BarElement, CategoryScale, Chart as ChartJS, ChartOptions, Filler, Legend,
    LinearScale, LineElement, PointElement, RadialLinearScale, Title, Tooltip,
    BarController, PieController, LineController, RadarController, BubbleController, ScatterController, DoughnutController, PolarAreaController
} from 'chart.js';
import type { ChartType } from 'chart.js';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import { renderClasses } from '@agilepoint/utils-common';

import type { IChartProps } from './types';
import { setDatasets } from './utils';
import { useChooseRef } from '../../utils';

const DATASET_ID_KEY = 'id';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
    Filler,
    BarController,
    PieController,
    LineController,
    RadarController,
    BubbleController,
    ScatterController,
    DoughnutController,
    PolarAreaController
);

export function Chart<TType extends ChartType = ChartType>(props: IChartProps<TType>) {
    const {
        className,
        type,
        data,
        updateMode = 'active',
        elementRef,
        chartTitle,
        children,
        labels,
        titleAlignment = 'center',
        titlePosition = 'top',
        onClick, onHover, onResize,
        ...rest
    } = props;
    type TypedChartJS = ChartJS<TType>;

    const canvasRef = useChooseRef<HTMLCanvasElement>(elementRef);
    const chartRef = useRef<TypedChartJS | null>();
    const { chart: { colors, composites } } = useTheme();

    const options = useMemo(() => ({
        responsive: true,
        plugins: {
            title: {
                display: !!chartTitle,
                align: titleAlignment,
                position: titlePosition,
                text: chartTitle,
                color: colors.titleColor,
                padding: composites.titlePadding
            }
        },
        onClick, onHover, onResize,
    } as ChartOptions<TType>), [chartTitle, colors.titleColor, composites.titlePadding, onClick, onHover, onResize, titleAlignment, titlePosition]);

    const renderChartRef = useRef(() => { return; });
    renderChartRef.current = () => {
        if (!canvasRef.current) return;
        chartRef.current = new ChartJS(canvasRef.current, {
            type,
            data: {
                labels,
                datasets: data
            },
            options,
            plugins: [],
        });
    };

    const destroyChartRef = useRef(() => {
        if (!chartRef.current) return;
        chartRef.current.destroy();
        chartRef.current = null;
    });

    useEffect(() => {
        const optionsObj = chartRef.current?.options;
        if (optionsObj && options) {
            Object.assign(optionsObj, options);
        }
    }, [options]);

    useEffect(() => {
        const configData = chartRef.current?.config.data;
        if (configData) {
            configData.labels = labels;
        }
    }, [labels]);

    useEffect(() => {
        const configData = chartRef.current?.config.data;
        if (configData && data) {
            setDatasets(configData, data as any, DATASET_ID_KEY);
        }
    }, [data]);

    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.update(updateMode);
    }, [options, labels, data, updateMode]);

    useEffect(() => {
        if (!chartRef.current) return;
        destroyChartRef.current();
        setTimeout(renderChartRef.current);
    }, [type]);

    useEffect(() => {
        renderChartRef.current();
        return destroyChartRef.current;
    }, []);

    return (
        <canvas className={renderClasses(className ? [className] : [], 'ap-chart')} ref={canvasRef} role='img' {...rest}>
            {children}
        </canvas>
    );
}